import React, { type PropsWithChildren } from 'react';
import { Drawer, Typography, ConfigProvider, Spin } from 'antd';
import { CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import Button from '../Button';
import { createPrimaryTheme } from '../../theme/antd-theme';
import styles from '../../style/components/modal-wrapper.module.scss';

const { Title } = Typography;

interface ModalWrapperProps {
  open: boolean;
  onClose: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  onDelete?: () => void;
  title?: string;
  width?: number;
  saveButtonText?: string;
  cancelButtonText?: string;
  saveLoading?: boolean;
  loading?: boolean;
  deleteButton?: boolean;
  hideFooter?: boolean;
  hideSaveButton?: boolean;
}

const ModalWrapper: React.FC<PropsWithChildren<ModalWrapperProps>> = ({
  children,
  open,
  onClose,
  onSave,
  onCancel,
  onDelete,
  title = 'Modal',
  width = 400,
  saveButtonText = 'Save',
  cancelButtonText = 'Cancel',
  saveLoading = false,
  loading = false,
  deleteButton = false,
  hideFooter = false,
  hideSaveButton = false,
}) => {
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave();
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
  };

  return (
    <ConfigProvider theme={createPrimaryTheme()}>
      <Drawer
        title={null}
        placement="right"
        onClose={onClose}
        open={open}
        width={width}
        className={styles.modalWrapper}
        closeIcon={false}
        mask={true}
        maskClosable={true}
      >
        <div className={styles.drawerContent}>
          <div className={styles.header}>
            <Title
              level={4}
              className={styles.title}
            >
              {title}
            </Title>
            <button
              className={styles.closeButton}
              onClick={onClose}
            >
              <CloseOutlined />
            </button>
          </div>

          <div className={styles.content}>
            <Spin spinning={loading}>{children}</Spin>
          </div>

          {!hideFooter && (
            <div className={styles.footer}>
              <div className={styles.buttonContainer}>
                {deleteButton && (
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={handleDelete}
                  >
                    Delete
                  </Button>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                  <Button
                    theme="secondary"
                    onClick={handleCancel}
                  >
                    {cancelButtonText}
                  </Button>
                  {!hideSaveButton && (
                    <Button
                      theme="primary"
                      onClick={handleSave}
                      loading={saveLoading}
                    >
                      {saveButtonText}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </Drawer>
    </ConfigProvider>
  );
};

export default ModalWrapper;

